// ============================================================================
// SECOND SCREEN GRID - react-grid-layout wrapper
// Reads layout from localStorage, renders widgets in static mode
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import WidgetShell from './WidgetShell';
import { WIDGET_REGISTRY } from './widgets/widgetRegistry';
import { PRESET_LAYOUTS } from './presets';
import type { SecondScreenLayout, WidgetInstance } from '../../types/second-screen.types';

const LAYOUT_KEY_PREFIX = 'second-screen-layout-';

function storageKey(prefix: string, sessionId: string, screenNumber: number): string {
  return `${prefix}${sessionId}-screen${screenNumber}`;
}

function getStoredLayout(sessionId: string, screenNumber: number): SecondScreenLayout | null {
  try {
    const raw = localStorage.getItem(storageKey(LAYOUT_KEY_PREFIX, sessionId, screenNumber));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

interface SecondScreenGridProps {
  sessionId: string;
  screenNumber: number;
}

const MARGIN: [number, number] = [8, 8];
const CONTAINER_PADDING: [number, number] = [8, 8];

const SecondScreenGrid: React.FC<SecondScreenGridProps> = ({ sessionId, screenNumber }) => {
  const [layout, setLayout] = useState<SecondScreenLayout>(() => {
    return getStoredLayout(sessionId, screenNumber) || PRESET_LAYOUTS[0];
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 1920, height: 1080 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ width: Math.round(width), height: Math.round(height) });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Listen for layout changes from main window via localStorage
  useEffect(() => {
    const layoutKey = storageKey(LAYOUT_KEY_PREFIX, sessionId, screenNumber);
    const handleStorage = (e: StorageEvent) => {
      if (e.key === layoutKey && e.newValue) {
        try {
          setLayout(JSON.parse(e.newValue));
        } catch { /* ignore parse errors */ }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [sessionId, screenNumber]);

  const cols = 12;
  const totalRows = useMemo(() => {
    return layout.widgets.reduce((max, w) => Math.max(max, w.y + w.h), 1);
  }, [layout.widgets]);

  const rowHeight = useMemo(() => {
    const gapSpace = (totalRows - 1) * MARGIN[1] + 2 * CONTAINER_PADDING[1];
    return Math.floor((containerSize.height - gapSpace) / totalRows);
  }, [totalRows, containerSize.height]);

  const gridLayout = useMemo(() => {
    return layout.widgets.map((w: WidgetInstance) => ({
      i: w.instanceId,
      x: w.x,
      y: w.y,
      w: w.w,
      h: w.h,
      static: true, // Display-only in popup
    }));
  }, [layout.widgets]);

  const renderWidget = useCallback((widget: WidgetInstance) => {
    const definition = WIDGET_REGISTRY[widget.widgetId];
    if (!definition) {
      return (
        <div key={widget.instanceId} className="bg-red-500/10 rounded-xl border border-red-500/30 flex items-center justify-center text-red-400 text-sm">
          Unknown widget: {widget.widgetId}
        </div>
      );
    }

    return (
      <div key={widget.instanceId}>
        <WidgetShell
          label={definition.label}
          widgetId={widget.widgetId}
          config={widget.config}
          Component={definition.component}
        />
      </div>
    );
  }, []);

  return (
    <div
      ref={containerRef}
      className="w-full overflow-hidden"
      style={{
        fontSize: `${Math.max(14, Math.round(containerSize.width / 120))}px`,
        height: '100%',
      }}
    >
      <GridLayout
        className="layout"
        layout={gridLayout}
        cols={cols}
        rowHeight={rowHeight}
        width={containerSize.width}
        margin={MARGIN}
        containerPadding={CONTAINER_PADDING}
        isDraggable={false}
        isResizable={false}
        compactType="vertical"
      >
        {layout.widgets.map(renderWidget)}
      </GridLayout>
    </div>
  );
};

export default SecondScreenGrid;
